import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon as XIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import Table from "../../common/Table";
import toast from "react-hot-toast";

const ManageInstitutionTypesModal = ({
  isOpen,
  onClose,
  onInstitutionTypeAdded,
}) => {
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTypeData, setNewTypeData] = useState({
    name: "",
    code: "",
    description: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingType, setEditingType] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    code: "",
    description: "",
  });

  useEffect(() => {
    if (isOpen) fetchInstitutionTypes();
  }, [isOpen]);

  const fetchInstitutionTypes = async () => {
    try {
      setLoading(true);
      const response = await institutionAPI.getListInstitutionTypes();
      const typesData = response.data?.data?.institution_types || [];
      const transformed = typesData.map((type) => ({
        id: type.id,
        name: type.name,
        code: type.code,
        description: type.description,
        institutionsCount: type.institutions_count || 0,
      }));
      setInstitutionTypes(transformed);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTypes = institutionTypes;

  const totalItems = filteredTypes.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const paginatedTypes = filteredTypes.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // --- Handlers intact ---
  const handleAddType = async () => {
    if (!newTypeData.name.trim() || !newTypeData.code.trim()) {
      toast.error("Name and Code are required");
      return;
    }
    try {
      const response = await institutionAPI.createInstitutionType(newTypeData);
      const newType = response.data.data || response.data;
      setInstitutionTypes((prev) => [{ id: newType.id, ...newType }, ...prev]);
      onInstitutionTypeAdded?.(newType);
      setNewTypeData({ name: "", code: "", description: "" });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveEdit = async () => {
    if (!editData.name.trim() || !editData.code.trim()) {
      toast.error("Name and Code required");
      return;
    }
    try {
      await institutionAPI.updateInstitutionType(editingType, editData);
      setInstitutionTypes((prev) =>
        prev.map((t) => (t.id === editingType ? { ...t, ...editData } : t))
      );
      setEditingType(null);
    } catch (error) {
      console.error(error);
    }
  };

  // --- DELETE FUNCTION FULLY INTACT ---
  const handleDeleteType = async (type) => {
    if (type.institutionsCount > 0) {
      toast.error("Cannot delete — this type is already used");
      return;
    }

    try {
      await institutionAPI.deleteInstitutionType(type.id);
      setInstitutionTypes((prev) => prev.filter((t) => t.id !== type.id));
      toast.success("Institution type deleted");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to delete institution type"
      );
    }
  };

  if (!isOpen) return null;

  const columns = [
    {
      key: "name",
      label: <span className="text-xs font-medium">INSTITUTION TYPE</span>,
      render: (_, row) =>
        editingType === row.id ? (
          <input
            value={editData.name}
            onChange={(e) =>
              setEditData((p) => ({ ...p, name: e.target.value }))
            }
            className="w-full border border-blue-400 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span className="text-sm">{row.name}</span>
        ),
    },
    {
      key: "code",
      label: <span className="text-xs font-medium">CODE</span>,
      render: (_, row) =>
        editingType === row.id ? (
          <input
            value={editData.code}
            onChange={(e) =>
              setEditData((p) => ({ ...p, code: e.target.value }))
            }
            className="w-full border border-blue-400 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span className="text-sm">{row.code}</span>
        ),
    },
    {
      key: "description",
      label: <span className="text-xs font-medium">DESCRIPTION</span>,
      render: (_, row) =>
        editingType === row.id ? (
          <textarea
            value={editData.description}
            onChange={(e) =>
              setEditData((p) => ({ ...p, description: e.target.value }))
            }
            rows={2}
            className="w-full border border-blue-400 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span className="text-sm">{row.description || "—"}</span>
        ),
    },
    {
      key: "actions",
      label: <span className="text-xs font-medium">ACTIONS</span>,
      render: (_, row) => (
        <div className="flex gap-3 text-sm">
          {editingType === row.id ? (
            <>
              <button
                onClick={handleSaveEdit}
                className="text-green-600 hover:text-green-700"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setEditingType(null)}
                className="text-red-500 hover:text-red-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setEditingType(row.id);
                  setEditData(row);
                }}
                className="text-yellow-500 hover:text-yellow-600"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteType(row)}
                disabled={row.institutionsCount > 0}
                title={
                  row.institutionsCount > 0
                    ? "Cannot delete — this type is already used"
                    : "Delete"
                }
                className={`p-1 rounded ${row.institutionsCount > 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-600 hover:text-red-900"
                  }`}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];



  return (
    <div className="fixed inset-0 z-[60] flex justify-center overflow-y-auto py-10">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>

      {/* <div className="relative bg-white rounded-xl w-full max-w-4xl shadow-2xl p-6 my-auto"> */}
      <div className="relative bg-white rounded-xl w-full max-w-4xl shadow-2xl my-auto flex flex-col max-h-[80vh]">
        {/* Header */}
        {/* <div className="flex justify-between items-center mb-6 border-b pb-4"> */}
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">
            Manage Institution Types
          </h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Add Section */}
        <div className="flex-1 overflow-y-auto">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddType();
          }}
          className="mb-6 p-4 rounded-lg"
        >
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Add New Institution Type
          </h3>

          <div className="flex gap-2 items-end">
            {/* Name */}
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter institution type name"
                value={newTypeData.name}
                onChange={(e) =>
                  setNewTypeData((p) => ({ ...p, name: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Code */}
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter institution type code"
                value={newTypeData.code}
                onChange={(e) =>
                  setNewTypeData((p) => ({ ...p, code: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Description */}
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                placeholder="Enter description (optional)"
                value={newTypeData.description}
                onChange={(e) =>
                  setNewTypeData((p) => ({ ...p, description: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Add Button */}
            <div>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium text-sm"
              >
                Add
              </button>
            </div>
          </div>
        </form>


        {/* Table */}
        <div className="p-6">
          <div className="rounded-lg">
            <Table
              columns={columns}
              data={paginatedTypes}
              getRowKey={(row) => row.id}
              pagination={{
                currentPage,
                lastPage: totalPages,
                perPage: rowsPerPage,
                total: totalItems,
              }}
              onPageChange={(page) => setCurrentPage(page)}
              paginationProps={{
                showSizeSelector: true,
                onPerPageChange: (size) => {
                  setRowsPerPage(size);
                  setCurrentPage(1);
                },
              }}
              className="min-w-full"
              noContainer={true}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ManageInstitutionTypesModal;
